/**
 * Local SQLite adapters for craftspaces.
 * Uses appDb — the app-level DB that holds the craftspaces registry.
 * Mirrors the Convex api.craftspaces.* interface.
 */
import { appDb, newId, now } from "@/lib/localDb";
import { invalidate } from "./queryCache";
import { useLocalQuery, useLocalGet } from "./useLocalQuery";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LocalCraftspace {
  _id: string;
  name: string;
  slug: string;
  storage_provider?: "360crafter" | "custom-s3";
  icon_url?: string;
  order?: number;
  s3_bucket_name?: string;
  s3_region?: string;
  s3_endpoint?: string;
  created_at?: string;
  updated_at?: string;
}

// ─── Queries ──────────────────────────────────────────────────────────────────
// dbTarget = undefined → routes to app.db

export function useCraftspaces(): LocalCraftspace[] | undefined {
  return useLocalQuery<LocalCraftspace>(
    "SELECT id as _id, * FROM craftspaces ORDER BY `order` ASC, name ASC",
    [],
    ["craftspaces"],
    [],
    null, // app.db
  );
}

export function useCraftspace(id: string | undefined): LocalCraftspace | null | undefined {
  return useLocalGet<LocalCraftspace>(
    "SELECT id as _id, * FROM craftspaces WHERE id = ?",
    [id ?? ""],
    ["craftspaces"],
    [],
    null, // app.db
  );
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export async function createCraftspace(params: {
  name: string;
  storage_provider?: "360crafter" | "custom-s3";
  icon_url?: string;
  order?: number;
  s3_bucket_name?: string;
  s3_region?: string;
  s3_endpoint?: string;
}): Promise<string> {
  const id = newId();
  const slug = params.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const ts = now();
  await appDb.run(
    `INSERT INTO craftspaces (id, name, slug, storage_provider, icon_url, \`order\`,
      s3_bucket_name, s3_region, s3_endpoint, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      id,
      params.name,
      slug,
      params.storage_provider ?? "360crafter",
      params.icon_url ?? null,
      params.order ?? null,
      params.s3_bucket_name ?? null,
      params.s3_region ?? null,
      params.s3_endpoint ?? null,
      ts,
      ts,
    ],
  );
  invalidate("craftspaces");
  return id;
}

export async function updateCraftspace(params: {
  id: string;
  name?: string;
  slug?: string;
  storage_provider?: "360crafter" | "custom-s3";
  icon_url?: string;
  order?: number;
  s3_bucket_name?: string;
  s3_region?: string;
  s3_endpoint?: string;
}): Promise<void> {
  const { id, ...fields } = params;
  const setClauses: string[] = ["updated_at = ?"];
  const values: unknown[] = [now()];

  if (fields.name !== undefined) { setClauses.push("name = ?"); values.push(fields.name); }
  if (fields.slug !== undefined) { setClauses.push("slug = ?"); values.push(fields.slug); }
  if (fields.storage_provider !== undefined) { setClauses.push("storage_provider = ?"); values.push(fields.storage_provider); }
  if (fields.icon_url !== undefined) { setClauses.push("icon_url = ?"); values.push(fields.icon_url); }
  if (fields.order !== undefined) { setClauses.push("`order` = ?"); values.push(fields.order); }
  if (fields.s3_bucket_name !== undefined) { setClauses.push("s3_bucket_name = ?"); values.push(fields.s3_bucket_name); }
  if (fields.s3_region !== undefined) { setClauses.push("s3_region = ?"); values.push(fields.s3_region); }
  if (fields.s3_endpoint !== undefined) { setClauses.push("s3_endpoint = ?"); values.push(fields.s3_endpoint); }

  values.push(id);
  await appDb.run(`UPDATE craftspaces SET ${setClauses.join(", ")} WHERE id = ?`, values);
  invalidate("craftspaces");
}

export async function removeCraftspace(id: string): Promise<void> {
  await appDb.run("DELETE FROM craftspaces WHERE id = ?", [id]);
  invalidate("craftspaces", "projects", "panoramas", "hotspots");
}

export async function reorderCraftspaces(items: Array<{ id: string; order: number }>): Promise<void> {
  await appDb.transaction(
    items.map(({ id, order }) => ({
      sql: "UPDATE craftspaces SET `order` = ?, updated_at = ? WHERE id = ?",
      params: [order, now(), id],
    })),
  );
  invalidate("craftspaces");
}

export async function createDefaultCraftspaceIfMissing(): Promise<string | null> {
  const existing = await appDb.get<{ id: string }>("SELECT id FROM craftspaces LIMIT 1");
  if (existing) return existing.id;
  return createCraftspace({ name: "My Craftspace" });
}

export async function getCraftspaceStorageProvider(id: string): Promise<"360crafter" | "custom-s3"> {
  const row = await appDb.get<{ storage_provider: string }>(
    "SELECT storage_provider FROM craftspaces WHERE id = ?",
    [id],
  );
  return (row?.storage_provider as "360crafter" | "custom-s3") ?? "360crafter";
}
